// Credit card validation utilities - Card type detection only

export interface CardValidation {
  isValid: boolean
  cardType: string
  error?: string
}

export interface ExpiryValidation {
  isValid: boolean
  error?: string
}

export interface CVVValidation {
  isValid: boolean
  error?: string
}

// Card type patterns based on industry standards
const cardPatterns = {
  visa: /^4/,
  mastercard: /^(5[1-5]|2[2-7])/,
  amex: /^3[47]/,
  discover: /^(6011|65|64[4-9]|622)/,
  dinersclub: /^(36|38|30[0-5])/,
  jcb: /^35/,
  unionpay: /^62/,
}

// Detect card type from number
export function detectCardType(cardNumber: string): string {
  const digits = cardNumber.replace(/\D/g, '')

  if (cardPatterns.visa.test(digits)) return 'Visa'
  if (cardPatterns.mastercard.test(digits)) return 'Mastercard'
  if (cardPatterns.amex.test(digits)) return 'American Express'
  if (cardPatterns.discover.test(digits)) return 'Discover'
  if (cardPatterns.dinersclub.test(digits)) return 'Diners Club'
  if (cardPatterns.jcb.test(digits)) return 'JCB'
  if (cardPatterns.unionpay.test(digits)) return 'UnionPay'

  return 'Unknown'
}

// Get expected card length based on type
function getExpectedLength(cardType: string): number {
  switch (cardType) {
    case 'American Express':
      return 15
    case 'Diners Club':
      return 14
    default:
      return 16
  }
}

// Format card number with spaces
export function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '')
  const cardType = detectCardType(digits)

  // American Express: 4-6-5 format
  if (cardType === 'American Express') {
    return digits
      .slice(0, 15)
      .replace(/(\d{4})(\d{0,6})(\d{0,5})/, (match, p1, p2, p3) => {
        let formatted = p1
        if (p2) formatted += ' ' + p2
        if (p3) formatted += ' ' + p3
        return formatted
      })
      .trim()
  }

  // Diners Club: 4-6-4 format
  if (cardType === 'Diners Club') {
    return digits
      .slice(0, 14)
      .replace(/(\d{4})(\d{0,6})(\d{0,4})/, (match, p1, p2, p3) => {
        let formatted = p1
        if (p2) formatted += ' ' + p2
        if (p3) formatted += ' ' + p3
        return formatted
      })
      .trim()
  }

  // Other cards: 4-4-4-4 format
  return digits
    .slice(0, 16)
    .replace(/(\d{4})/g, '$1 ')
    .trim()
}

// Validate card number - only checks type detection and length
export function validateCardNumber(cardNumber: string): CardValidation {
  const digits = cardNumber.replace(/\D/g, '')

  if (digits.length === 0) {
    return {
      isValid: false,
      cardType: 'Unknown',
      error: 'Card number is required',
    }
  }

  const cardType = detectCardType(digits)

  if (cardType === 'Unknown') {
    return {
      isValid: false,
      cardType: 'Unknown',
      error: 'Card type not recognized',
    }
  }

  const expectedLength = getExpectedLength(cardType)

  if (digits.length < expectedLength) {
    return {
      isValid: false,
      cardType,
      error: `${cardType} cards must be ${expectedLength} digits`,
    }
  }

  if (digits.length > expectedLength) {
    return {
      isValid: false,
      cardType,
      error: `Card number is too long`,
    }
  }

  return { isValid: true, cardType }
}

// Format expiry date
export function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length >= 2) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`
  }
  return digits
}

// Validate expiry date
export function validateExpiry(expiry: string): ExpiryValidation {
  const digits = expiry.replace(/\D/g, '')

  if (digits.length === 0) {
    return { isValid: false, error: 'Expiry date is required' }
  }

  if (digits.length !== 4) {
    return { isValid: false, error: 'Expiry date must be MM/YY format' }
  }

  const month = parseInt(digits.slice(0, 2))
  const year = parseInt(digits.slice(2, 4))

  if (month < 1 || month > 12) {
    return { isValid: false, error: 'Invalid month' }
  }

  const currentDate = new Date()
  const currentYear = currentDate.getFullYear() % 100
  const currentMonth = currentDate.getMonth() + 1

  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return { isValid: false, error: 'Card has expired' }
  }

  return { isValid: true }
}

// Format CVV
export function formatCVV(value: string, cardType: string): string {
  const digits = value.replace(/\D/g, '')
  const maxLength = cardType === 'American Express' ? 4 : 3
  return digits.slice(0, maxLength)
}

// Validate CVV
export function validateCVV(cvv: string, cardType: string): CVVValidation {
  const digits = cvv.replace(/\D/g, '')

  if (digits.length === 0) {
    return { isValid: false, error: 'CVV is required' }
  }

  const expectedLength = cardType === 'American Express' ? 4 : 3

  if (digits.length !== expectedLength) {
    return {
      isValid: false,
      error: `CVV must be ${expectedLength} digits`,
    }
  }

  return { isValid: true }
}

// Validate cardholder name
export function validateCardholderName(name: string): {
  isValid: boolean
  error?: string
} {
  const trimmed = name.trim()

  if (trimmed.length === 0) {
    return { isValid: false, error: 'Cardholder name is required' }
  }

  if (trimmed.length < 3) {
    return { isValid: false, error: 'Name is too short' }
  }

  if (!/^[a-zA-Z\s'-]+$/.test(trimmed)) {
    return {
      isValid: false,
      error: 'Name can only contain letters, spaces, hyphens, and apostrophes',
    }
  }

  return { isValid: true }
}
